import { useState } from "react"
import { DateTime } from "luxon"
import { Box, Stack, TextField, Typography } from "@mui/material"
import { TimePicker } from "@mui/x-date-pickers"
import { makeStyles } from "tss-react/mui"

import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { ApiQueryAutocomplete } from "../../../../../../../core/api/components/ApiQueryAutocomplete"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../../../localization/useDateTime"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { StandardAlertInfo } from "../../../../../../../shared/components/alert/StandardAlertInfo"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import {
    ConfigQuery,
    fetchMeetingServiceConfiguration,
    fetchTimelineResources,
    type MeetingServiceConfigurationResponse,
    type TimelineResourcesResponse,
} from "../../../../../../../shared/components/createOrder/createOrderApi"
import { ExtraServiceDetails, ResourceType } from "../../../../../../../shared/components/createOrder/createOrderTypes"
import {
    DeliveryFulfillmentType,
    type DynamicConfigField,
    DynamicConfigKey,
} from "../../../../../../../shared/components/createOrder/components/form/formTypes"
import { roundToNearest5Minutes } from "../../../../../../../shared/components/createOrder/components/helpers"
import { DEFAULT_DURATION_MINUTES } from "../../../../../../../shared/components/createOrder/components/resource/resourceBookingCalendars/helpers"
import {
    DeliveryLocationType,
    type DeliveryLocationInfo,
} from "../../../../../../../shared/components/dialogs/receiptConfirmOrderDialog/receiptConfirmOrderDialogTypes"
import { ProductionAddLookup, ProductionMeetingExtras } from "../useProductionOrderEdit"

const useStyles = makeStyles()((theme) => ({
    // Mirrors Create order's additional-fields block: divider on top, 1rem between the rows.
    root: {
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
        paddingTop: "1rem",
    },
    title: {
        fontWeight: 600,
        color: theme.palette.tailwind.slate[800],
    },
    // All fields on one line; on tablets/phones the location drops to its own line.
    fieldsRow: {
        [theme.breakpoints.down("md")]: {
            flexWrap: "wrap",
        },
    },
    timeField: {
        width: "10rem",
        flexShrink: 0,
    },
    locationField: {
        flexGrow: 1,
        minWidth: "14rem",
    },
    otherLocationLink: {
        textDecoration: "underline",
        "&:hover": {
            background: "transparent",
        },
    },
}))

// The delivery-location field labels come from the merchant's dynamic config, exactly as in Create order.
const selectDeliveryFieldConfigs = (
    data: MeetingServiceConfigurationResponse,
): Readonly<{ deliveryLocations?: DynamicConfigField; otherDeliveryLocation?: DynamicConfigField }> => {
    const allFields = (data.meetingServiceConfig?.sections ?? []).flatMap((section) =>
        section.groups.flatMap((group) => group.fields),
    )
    return {
        deliveryLocations: allFields.find((field) => field.key === DynamicConfigKey.DELIVERY_LOCATIONS),
        otherDeliveryLocation: allFields.find((field) => field.key === DynamicConfigKey.OTHER_DELIVERY_LOCATION),
    }
}

/** A bookable room offered as the meeting location, as in Create order. */
type LocationResource = Readonly<{
    id: number
    name: string
    serviceCenterName: string
}>

// MUI groups options based on the order they are provided, so keep them sorted by group label.
const selectLocationResources = (data: TimelineResourcesResponse): LocationResource[] =>
    (data?.serviceCenters ?? [])
        .flatMap((serviceCenter) =>
            serviceCenter.resources.map((resource) => ({
                id: resource.id,
                name: resource.displayName || resource.name,
                serviceCenterName: serviceCenter.name,
            })),
        )
        .sort((a, b) => a.serviceCenterName.localeCompare(b.serviceCenterName) || a.name.localeCompare(b.name))

/** Prefills for the additional meeting details asked for by time/location-required services. */
export type ProductionMeetingDefaults = Readonly<{
    /** Date of the edited delivery — the entered times are anchored to it. */
    deliveryDate: DateTime
    start: DateTime | null
    end: DateTime | null
}>

type Props = Readonly<{
    /** The picked time/location-required service the details are being collected for. */
    service: ExtraServiceDetails
    lookup: ProductionAddLookup
    meetingDefaults: ProductionMeetingDefaults
    onConfirm: (extras: ProductionMeetingExtras) => void
    onCancel: () => void
}>

/**
 * The "Additional meeting details" block shown when a picked extra service requires a meeting time and/or
 * location, mirroring the MEETING_SERVICE flow in Create order: time pickers prefilled from the order and a
 * room autocomplete with a free-text "other location" fallback.
 */
export function ProductionMeetingDetailsFields({ service, lookup, meetingDefaults, onConfirm, onCancel }: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()

    const [startTime, setStartTime] = useState<DateTime | null>(meetingDefaults.start)
    const [endTime, setEndTime] = useState<DateTime | null>(meetingDefaults.end)
    const [locationResource, setLocationResource] = useState<LocationResource | null>(null)
    const [otherLocationName, setOtherLocationName] = useState("")
    const [showOtherLocation, setShowOtherLocation] = useState(false)
    const [confirmAttempted, setConfirmAttempted] = useState(false)

    const needsTime = Boolean(service.isMeetingTimeRequired)
    const needsLocation = Boolean(service.isMeetingLocationRequired)

    const configQuery = useApiQuery(fetchMeetingServiceConfiguration, {
        queryName: ConfigQuery.MEETING_SERVICE_CONFIG,
        dependencies: { userId: lookup.userId, merchantId: lookup.merchantId },
        enabled: needsLocation,
        select: selectDeliveryFieldConfigs,
    })

    // Same source as Create order's location field: the user's bookable rooms, grouped by service center.
    const resourcesQuery = useApiQuery(fetchTimelineResources, {
        queryName: ConfigQuery.TIMELINE_RESOURCES_FOR_USER,
        dependencies: { userId: lookup.userId, resourceTypes: ResourceType.ROOM },
        enabled: needsLocation,
        select: selectLocationResources,
    })
    const locationOptions = resourcesQuery.data ?? []
    // No rooms to pick from → the free-text field is the only location input, as in Create order.
    const hasLocationOptions = !(needsLocation && !resourcesQuery.isInitialLoading && locationOptions.length === 0)

    // Match Create order: picking a start with no (or earlier) end moves the end to start + default duration.
    const handleStartChange = (value: DateTime | null) => {
        if (!value) {
            setStartTime(null)
            return
        }
        const rounded = roundToNearest5Minutes(value)
        setStartTime(rounded)
        if (!endTime || rounded >= endTime) {
            setEndTime(rounded.plus({ minutes: DEFAULT_DURATION_MINUTES }))
        }
    }

    const handleOtherLocationChange = (value: string) => {
        setOtherLocationName(value)
        if (value) setLocationResource(null)
    }

    // A picked room wins over free text, matching how Create order builds the payload.
    const selectedLocation: DeliveryLocationInfo | undefined = locationResource
        ? { type: DeliveryLocationType.RESOURCE, resourceId: locationResource.id }
        : otherLocationName.trim()
          ? { type: DeliveryLocationType.OTHER_LOCATION, name: otherLocationName.trim() }
          : undefined

    const isTimeMissing = needsTime && (!startTime || !endTime)
    const isLocationMissing = needsLocation && !selectedLocation

    // The entered times are times of day; anchor them to the delivery's date for the API.
    const toMeetingApiTime = (time: DateTime) =>
        dateTimeFactory.toApi(
            meetingDefaults.deliveryDate.set({ hour: time.hour, minute: time.minute, second: 0, millisecond: 0 }),
        )

    const handleConfirm = () => {
        setConfirmAttempted(true)
        if (isTimeMissing || isLocationMissing) return
        onConfirm({
            ...(needsTime && startTime && endTime
                ? {
                      meetingTime: {
                          startDateTime: toMeetingApiTime(startTime),
                          endDateTime: toMeetingApiTime(endTime),
                      },
                  }
                : {}),
            ...(selectedLocation ? { deliveryLocation: selectedLocation } : {}),
        })
    }

    const requiredError = l10n.getString("meeting-service-production-view-add-service-required-error")
    // Label rules mirror Create order: for a DELIVERY order the config's own label (e.g. "Leveringslokation:"),
    // otherwise the meeting-location label.
    const isMeetingLocation = lookup.deliveryType !== DeliveryFulfillmentType.DELIVERY
    const locationConfig = configQuery.data?.deliveryLocations
    const otherLocationConfig = configQuery.data?.otherDeliveryLocation
    const meetingLocationLabel = l10n.getString("create-order-drawer-meeting-location-label")
    const locationBaseLabel = isMeetingLocation
        ? meetingLocationLabel
        : locationConfig?.label || locationConfig?.description || meetingLocationLabel
    const locationLabel = locationBaseLabel
    const otherLocationBaseLabel = isMeetingLocation
        ? meetingLocationLabel
        : otherLocationConfig?.label || otherLocationConfig?.description || meetingLocationLabel
    const otherLocationLabel = otherLocationBaseLabel

    const otherLocationFieldProps = {
        size: "small" as const,
        label: otherLocationLabel,
        required: true,
        // The dialog validates on confirm; the native attribute would let the browser block it first.
        inputProps: { required: false },
        value: otherLocationName,
        error: confirmAttempted && isLocationMissing,
        helperText: confirmAttempted && isLocationMissing ? requiredError : undefined,
    }

    return (
        <Stack spacing="1rem" className={classes.root}>
            <LocalizedStrict id="create-order-extra-services-additional-fields-title">
                <Typography variant="body1" className={classes.title}>
                    Additional meeting details:
                </Typography>
            </LocalizedStrict>
            <LocalizedStrict id="create-order-extra-services-additional-fields-intro" attrs={{ title: true }}>
                <StandardAlertInfo title="" />
            </LocalizedStrict>
            <Stack direction="row" spacing="1rem" useFlexGap className={classes.fieldsRow}>
                {needsTime && (
                    <>
                        <TimePicker
                            value={startTime}
                            label={l10n.getString("create-order-extra-services-additional-fields-start-time-label")}
                            ampm={false}
                            minutesStep={5}
                            onChange={handleStartChange}
                            slotProps={{
                                textField: {
                                    size: "small",
                                    InputLabelProps: { required: true },
                                    className: classes.timeField,
                                    error: confirmAttempted && !startTime,
                                    helperText: confirmAttempted && !startTime ? requiredError : undefined,
                                },
                            }}
                        />
                        <TimePicker
                            value={endTime}
                            label={l10n.getString("create-order-extra-services-additional-fields-end-time-label")}
                            ampm={false}
                            minutesStep={5}
                            minTime={startTime ?? undefined}
                            onChange={setEndTime}
                            slotProps={{
                                textField: {
                                    size: "small",
                                    InputLabelProps: { required: true },
                                    className: classes.timeField,
                                    error: confirmAttempted && !endTime,
                                    helperText: confirmAttempted && !endTime ? requiredError : undefined,
                                },
                            }}
                        />
                    </>
                )}
                {needsLocation && hasLocationOptions && (
                    <ApiQueryAutocomplete<LocationResource>
                        className={classes.locationField}
                        size="small"
                        value={locationResource}
                        options={locationOptions}
                        isLoading={resourcesQuery.isInitialLoading}
                        isError={resourcesQuery.isError}
                        onRetry={() => resourcesQuery.refetch()}
                        label={locationLabel}
                        groupBy={(option) => option.serviceCenterName}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        onChange={(_event, option) => {
                            setLocationResource(option)
                            if (option) {
                                setOtherLocationName("")
                                setShowOtherLocation(false)
                            }
                        }}
                        slotProps={{
                            textField: {
                                required: true,
                                inputProps: { required: false },
                                error: confirmAttempted && isLocationMissing,
                                helperText: confirmAttempted && isLocationMissing ? requiredError : undefined,
                            },
                        }}
                    />
                )}
                {needsLocation && !hasLocationOptions && (
                    <TextField
                        {...otherLocationFieldProps}
                        className={classes.locationField}
                        onChange={(event) => handleOtherLocationChange(event.target.value)}
                    />
                )}
            </Stack>
            {needsLocation && hasLocationOptions && showOtherLocation && (
                <TextField
                    {...otherLocationFieldProps}
                    fullWidth
                    onChange={(event) => handleOtherLocationChange(event.target.value)}
                />
            )}
            {needsLocation && hasLocationOptions && !showOtherLocation && (
                <Box>
                    <LocalizedStrict id="create-order-drawer-other-location">
                        <StandardButton
                            type="button"
                            color="blue"
                            variant="text"
                            size="small"
                            fullWidth={false}
                            updateDefaultClasses={classes.otherLocationLink}
                            onClick={() => setShowOtherLocation(true)}
                        >
                            + Other location
                        </StandardButton>
                    </LocalizedStrict>
                </Box>
            )}
            <Stack direction="row" spacing=".75rem" justifyContent="flex-end">
                <LocalizedStrict id="meeting-service-production-view-add-picker-cancel">
                    <StandardButton fullWidth={false} variant="outlined" onClick={onCancel}>
                        Cancel
                    </StandardButton>
                </LocalizedStrict>
                <LocalizedStrict id="meeting-service-production-view-add-service-confirm-button">
                    <StandardButton fullWidth={false} variant="contained" onClick={handleConfirm}>
                        Add
                    </StandardButton>
                </LocalizedStrict>
            </Stack>
        </Stack>
    )
}
